import { useState, useEffect, Fragment, useCallback } from 'react';
import clienteAxios from '../../config/axios';
import NumberFormat from 'react-number-format';
import Mensaje from 'components/Alertas/Mensaje';
import LineSpinner from 'components/spinners/LineSpinner';
import styled, { keyframes } from 'styled-components';
import { useT } from 'i18n';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// Styled Components
const Container = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
`;

const InfoCard = styled.div`
  background: ${props => props.theme === 'white' 
    ? 'rgba(0, 0, 0, 0.03)' 
    : 'rgba(227,168,43, 0.1)'};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${props => props.theme === 'white' 
    ? 'rgba(0, 0, 0, 0.05)' 
    : 'rgba(227,168,43, 0.2)'};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: rgba(227,168,43,.14);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Subtitle = styled.p`
  color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 0.9rem;
`;

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const RequirementItem = styled.div`
  background: ${props => props.theme === 'white' 
    ? '#fff' 
    : 'rgba(12,14,19, 0.6)'};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  border-left: 3px solid #e3a82b;
  
  strong {
    color: #e3a82b;
    display: block;
    margin-bottom: 0.25rem;
  }
  
  small {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.6)'};
    font-size: 0.8rem;
  }
`;

const FormContainer = styled.div`
  background: ${props => props.theme === 'white' 
    ? '#fff' 
    : 'rgba(12,14,19, 0.6)'};
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid ${props => props.theme === 'white' 
    ? 'rgba(0, 0, 0, 0.1)' 
    : 'rgba(227,168,43, 0.2)'};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: ${props => props.theme === 'white' ? '#333' : '#fff'};
    
    i {
      margin-right: 0.5rem;
      color: #e3a82b;
    }
  }
  
  input {
    width: 100%;
    padding: 0.875rem 1rem;
    border-radius: 12px;
    border: 1px solid ${props => props.theme === 'white' 
      ? 'rgba(0, 0, 0, 0.2)' 
      : 'rgba(227,168,43, 0.3)'};
    background: ${props => props.theme === 'white' 
      ? '#f5f5f5' 
      : 'rgba(12,14,19, 0.8)'};
    color: ${props => props.theme === 'white' ? '#333' : '#fff'};
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #e3a82b;
      box-shadow: 0 0 10px rgba(227,168,43, 0.3);
    }
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => props.theme === 'white' 
    ? 'rgba(0, 0, 0, 0.2)' 
    : 'rgba(227,168,43, 0.3)'};
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #e3a82b;
    background: ${props => props.theme === 'white' 
      ? 'rgba(227,168,43, 0.05)' 
      : 'rgba(227,168,43, 0.1)'};
  }
  
  input {
    display: none;
  }
  
  label {
    cursor: pointer;
    margin: 0;
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 50px;
  border: none;
  background: rgba(227,168,43,.14);
  color: #e3a82b;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(227,168,43, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PreviewText = styled.span`
  display: inline-block;
  margin-top: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.theme === 'white' 
    ? 'rgba(0, 0, 0, 0.05)' 
    : 'rgba(227,168,43, 0.1)'};
  border-radius: 8px;
  font-size: 0.85rem;
  color: #e3a82b;
  animation: ${pulse} 1.5s infinite;
`;

const DepositosPanama = ({ jimf_theme, onCreado }) => {
  const { t, ts } = useT();
  const [creado, setCreado] = useState(false);
  const [spin, setSpin] = useState(false);
  const [mensaje, guardarMensaje] = useState('');
  const [mensajeopen, guardarMensajeOpen] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  const [deposit, guardarDeposit] = useState({
    amount: '',
    type: '2',
    file: null,
    direccion: '',
    hash: ''
  });

  const { amount, file, direccion, hash } = deposit;
  const isWhiteTheme = jimf_theme === 'white';

  const [direcciones, setDirecciones] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let vivo = true;
    clienteAxios.post('/api/client/deposits/direcciones')
      .then((r) => {
        const lista = r.data?.data || [];
        if (!vivo) return;
        setDirecciones(lista);
        if (lista.length === 1) guardarDeposit((d) => ({ ...d, direccion: lista[0].direccion }));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const consultarApiDeposit = useCallback(async () => {
    const formData = new FormData();
    formData.append('amount', deposit.amount);
    formData.append('direccion', deposit.direccion);
    formData.append('hash', deposit.hash);
    formData.append('type', '2');

    if (deposit.file && deposit.file instanceof File) {
      formData.append('file', deposit.file);
    } else {
      const fileInput = document.getElementById('file');
      if (fileInput?.files[0]) {
        formData.append('file', fileInput.files[0]);
      }
    }

    const config = {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json'
      }
    };

    try {
      const respuesta = await clienteAxios.post('/api/client/deposits/create', formData, config);
      guardarMensajeOpen(true);
      guardarMensaje(ts(respuesta.data.message));
      setCreado(true);
      
      // Limpiar formulario
      guardarDeposit({ amount: '', type: '2', file: null, direccion: '', hash: '' });
      setFilePreview(null);
      
      const fileInput = document.getElementById('file');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      guardarMensajeOpen(true);
      const errorMsg = ts(error.response?.data?.message) || t('dep.errorSolicitud');
      guardarMensaje(errorMsg);
    } finally {
      setSpin(false);
    }
  }, [deposit, t, ts]);

  const onChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'file' && files?.[0]) {
      const selectedFile = files[0];
      // Validar tamaño (4MB)
      if (selectedFile.size > 4 * 1024 * 1024) {
        guardarMensajeOpen(true);
        guardarMensaje(t('dep.archivoGrande4'));
        return;
      }
      
      // Validar tipo
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        guardarMensajeOpen(true);
        guardarMensaje(t('dep.formatoNoPermitido'));
        return;
      }
      
      guardarDeposit({ ...deposit, [name]: selectedFile });
      
      // Previsualización para imágenes
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    } else {
      guardarDeposit({ ...deposit, [name]: value });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    
    if (!deposit.amount || deposit.amount <= 0) {
      guardarMensajeOpen(true);
      guardarMensaje(t('dep.montoInvalido'));
      return;
    }
    
    if (!deposit.direccion?.trim()) {
      guardarMensajeOpen(true);
      guardarMensaje(t('dep.panSelDirMsg'));
      return;
    }
    
    if (!deposit.hash?.trim()) {
      guardarMensajeOpen(true);
      guardarMensaje(t('dep.panHashMsg'));
      return;
    }
    
    const fileInput = document.getElementById('file');
    if (!fileInput?.files[0]) {
      guardarMensajeOpen(true);
      guardarMensaje(t('dep.sinComprobante'));
      return;
    }
    
    setSpin(true);
    consultarApiDeposit();
  };

  const Loader = spin ? <LineSpinner /> : <Fragment>{t('dep.reportarBtn')}</Fragment>;

  return (
    <Container>
      {/* Información de ayuda */}
      <InfoCard theme={isWhiteTheme ? 'white' : 'dark'}>
        <Title>{t('dep.panTitulo')}</Title>
        <Subtitle theme={isWhiteTheme ? 'white' : 'dark'}>
          {t('dep.panSub')}
        </Subtitle>
        
        <RequirementsGrid>
          <RequirementItem theme={isWhiteTheme ? 'white' : 'dark'}>
            <strong>{t('dep.panReqMonto')}</strong>
            <small>{t('dep.panReqMontoSub')}</small>
          </RequirementItem>
          <RequirementItem theme={isWhiteTheme ? 'white' : 'dark'}>
            <strong>{t('dep.panReqDir')}</strong>
            <small>{t('dep.panReqDirSub')}</small>
          </RequirementItem>
          <RequirementItem theme={isWhiteTheme ? 'white' : 'dark'}>
            <strong>{t('dep.panReqHash')}</strong>
            <small>{t('dep.panReqHashSub')}</small>
          </RequirementItem>
          <RequirementItem theme={isWhiteTheme ? 'white' : 'dark'}>
            <strong>{t('dep.panReqComp')}</strong>
            <small>{t('dep.panReqCompSub')}</small>
          </RequirementItem>
        </RequirementsGrid>
      </InfoCard>

      {/* Formulario */}
      <FormContainer theme={isWhiteTheme ? 'white' : 'dark'}>
        <form onSubmit={onSubmit}>
          <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
            <label><i className="tim-icons icon-coins" /> {t('dep.panMonto')}</label>
            <input
              type="number"
              step="0.01"
              placeholder={t('dep.ejMonto100')}
              id="amount"
              name="amount"
              min="1"
              max="5000"
              onChange={onChange}
              value={amount}
              required
            />
            {amount && amount > 0 && (
              <PreviewText theme={isWhiteTheme ? 'white' : 'dark'}>
                {t('dep.estaReportando')} <NumberFormat 
                  value={amount} 
                  displayType={'text'} 
                  thousandSeparator={true} 
                  prefix={'USDT '} 
                  decimalScale={2} 
                  fixedDecimalScale={true}
                />
              </PreviewText>
            )}
          </FormGroup>

          <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
            <label><i className="tim-icons icon-wallet-43" /> {t('dep.panCualDir')}</label>
            <select id="direccion" name="direccion" onChange={onChange} value={direccion} required>
              <option value="">{t('dep.panSelDir')}</option>
              {direcciones.map((d) => (
                <option key={d.id || d.direccion} value={d.direccion}>
                  {(d.network || 'USDT')} — {d.direccion.slice(0, 8)}…{d.direccion.slice(-6)}
                </option>
              ))}
            </select>
            <small className="text-muted" style={{ display: 'block', marginTop: '0.5rem' }}>
              {t('dep.panDirAyuda')}
            </small>
          </FormGroup>

          <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
            <label><i className="tim-icons icon-link-72" /> {t('dep.panHashLabel')}</label>
            <input
              type="text"
              placeholder={t('dep.panHashPh')}
              id="hash"
              name="hash"
              onChange={onChange}
              value={hash}
              required
            />
          </FormGroup>

          <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
            <label><i className="tim-icons icon-cloud-upload-94" /> {t('dep.comprobante')}</label>
            <FileUploadArea theme={isWhiteTheme ? 'white' : 'dark'}>
              <input
                type="file"
                id="file"
                name="file"
                onChange={onChange}
                accept="image/*,.pdf"
              />
              <label htmlFor="file">
                {file ? (
                  <Fragment>
                    <i className="tim-icons icon-check-2" style={{ color: '#e3a82b' }} />
                    {' '}{file.name}
                  </Fragment>
                ) : (
                  <Fragment>
                    <i className="tim-icons icon-cloud-upload-94" />
                    {' '}{t('dep.clickCargar')}
                  </Fragment>
                )}
              </label>
              {filePreview && (
                <div style={{ marginTop: '1rem' }}>
                  <img src={filePreview} alt={t('dep.vistaPrevia')} style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} />
                </div>
              )}
            </FileUploadArea>
            <small className="text-muted" style={{ display: 'block', marginTop: '0.5rem' }}>
              {t('dep.formatosPermitidos')}
            </small>
          </FormGroup>

          <SubmitButton type="submit" disabled={spin}>
            {Loader}
          </SubmitButton>
        </form>
      </FormContainer>

      {mensajeopen && (
        <Mensaje
          open={true}
          mensaje={mensaje}
          guardarMensajeOpen={(v) => {
            guardarMensajeOpen(v);
            if (!v && creado) { setCreado(false); if (onCreado) onCreado(); }
          }}
          guardarMensaje={guardarMensaje}
        />
      )}
    </Container>
  );
};

export default DepositosPanama;